import { Box, Switch, Typography } from '@mui/material'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useLayout } from 'hooks/useLayout'
import {
  dataTableAddressFormatter,
  dataTableLicenseNumberFormatter,
  formatDateTime,
} from 'utils/util'
import {
  type CorporateAccount,
  type LicensedEstablishment,
  type Organization,
} from 'types/api'
import { CorporateAccountsTreeDataGrid } from 'components/CorporateAccountsTreeDataGrid/CorporateAccountsTreeDataGrid'
import { get, isNil, last } from 'lodash'
import { type components } from 'api/playerPayback/api'
import { colorPrimitives } from 'components/Theme'
import NotFoundLocation from 'assets/locationNotFound.svg'
import { useState } from 'react'
import { useSnackbar } from 'stores/useSnackbar'
import { usePutLocationSweepstake } from 'hooks/api/usePutLocationSweepstake'
import { usePutLocationSweepstakeGroup } from 'hooks/api/usePutLocationSweepstakeGroup'
import { CellLink } from 'components/CellLink/CellLink'

export interface ParticipatingLocation {
  name: string
  licenseNumber: string
  id: number
  addresses: any
  dateAdded: string
  le: LicensedEstablishment
}

const defaultColumns = (
  isMobile: boolean,
  sweepstake: components['schemas']['FullContestDTO'],
  setSelectedSwitchId?: (id: number, email: string) => void
): GridColDef[] => [
  {
    field: 'name',
    minWidth: 300,
    headerName: 'Account Name',
    flex: 1,
    renderCell: (params: GridRenderCellParams) =>
      params.row.type === 2 ? (
        <CellLink
          text={params.row.name}
          to={`/LicensedEstablishments/${Number(params.row.id)}`}
        />
      ) : (
        <span>{params.row.name}</span>
      ),
  },
  {
    field: 'licenseNumber',
    minWidth: 200,
    headerName: 'License #',
    flex: 0.8,
    valueFormatter: dataTableLicenseNumberFormatter(isMobile),
  },
  {
    field: 'addresses',
    minWidth: 420,
    headerName: 'Address',
    flex: 2,
    valueFormatter: dataTableAddressFormatter(true),
    renderCell: (params) => {
      return (
        <p className="overflow-hidden whitespace-nowrap text-ellipsis	">
          {params.formattedValue === 'Unspecified Address'
            ? '-'
            : params.formattedValue}
        </p>
      )
    },
  },
  {
    field: 'contacts.email',
    minWidth: 300,
    headerName: 'Email',
    flex: 1,
    renderCell: (params) => {
      const contacts = params.row.contacts as
        | Array<components['schemas']['FullLicensedEstablishmentContactDTO']>
        | undefined
      return isNil(contacts) || contacts.length === 0 ? (
        <>-</>
      ) : (
        <>
          {
            // Check if there are multiple contacts available
            contacts.length >= 2
              ? // If so, show the LocationSweepstakes contact
                last(
                  contacts.filter(
                    (x) => x.contactType === 'LocationSweepstakes'
                  )
                )?.email
              : // If not, show the Primary contact unless there isn't one then show the LocationSweepstakes contact
                (last(contacts.filter((x) => x.contactType === 'Primary'))
                  ?.email ??
                last(
                  contacts.filter(
                    (x) => x.contactType === 'LocationSweepstakes'
                  )
                )?.email)
          }
        </>
      )
    },
  },
  {
    field: 'dateAdded',
    headerName: 'Date Enrolled',
    flex: 1,
    minWidth: 175,
    renderCell: (params) => {
      const isLicensedEstablishment =
        get(params.row, 'corporateAccount') !== undefined &&
        get(params.row, 'organization') !== undefined
      return isLicensedEstablishment
        ? params.row.dateAdded
          ? formatDateTime(params.row.dateAdded, 'P p')
          : '-'
        : '-'
    },
  },
  {
    field: '__switch__',
    headerName: 'Preferred Email',
    flex: 1,
    align: 'right',
    minWidth: 150,
    renderCell: (params) => {
      const contacts = params.row.contacts as
        | Array<components['schemas']['FullLicensedEstablishmentContactDTO']>
        | undefined
      if (isNil(contacts) || contacts.length === 0) return '-'

      const isLicensedEstablishment =
        get(params.row, 'corporateAccount') !== undefined &&
        get(params.row, 'organization') !== undefined
      if (!isLicensedEstablishment) return '-'
      // Get the preferred email from the row (same logic as Email column)
      let preferredEmail = ''
      if (!isNil(contacts) && contacts.length > 0) {
        preferredEmail =
          contacts.length >= 2
            ? (last(
                contacts.filter((x) => x.contactType === 'LocationSweepstakes')
              )?.email ?? '')
            : (last(contacts.filter((x) => x.contactType === 'Primary'))
                ?.email ??
              last(
                contacts.filter((x) => x.contactType === 'LocationSweepstakes')
              )?.email ??
              '')
      }
      // Set switch to on if sweepstake.preferredEmail matches this row's preferredEmail
      const isChecked =
        (preferredEmail && preferredEmail === sweepstake.preferredEmail) ||
        false
      return (
        <Switch
          checked={isChecked}
          onChange={() => {
            if (isChecked) return // Prevent firing if already on
            setSelectedSwitchId?.(params.row.id, preferredEmail)
          }}
        />
      )
    },
  },
]

export const ParticipatingLocationList = ({
  les,
  organizations,
  corporateAccounts,
  sweepstake,
}: {
  les: LicensedEstablishment[]
  organizations: Organization[]
  corporateAccounts: CorporateAccount[]
  sweepstake: components['schemas']['FullContestDTO']
}) => {
  const { isMobile } = useLayout()
  const setMessage = useSnackbar((state) => state.setMessage)
  // Track which row's switch is on (by id)
  const [, setSelectedSwitchIdState] = useState<number | null>(null)

  const putLocationSweepstake = usePutLocationSweepstake({
    ...sweepstake,
    onSuccess: () => {
      setMessage('Preferred Email has been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
    onError: () => {
      setMessage('Error updating Preferred Email', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  const putLocationSweepstakeGroup = usePutLocationSweepstakeGroup({
    ...sweepstake,
    onSuccess: () => {
      setMessage('Preferred Email has been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
    onError: () => {
      setMessage('Error updating Preferred Email', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  // Handler to update preferred email and call mutation
  const setSelectedSwitchId = (id: number, email: string) => {
    setSelectedSwitchIdState(id)
    if (sweepstake.groupId != null) {
      putLocationSweepstakeGroup.mutate({
        ...sweepstake,
        preferredEmail: email,
      })
    } else {
      putLocationSweepstake.mutate({
        ...sweepstake,
        preferredEmail: email,
      })
    }
  }

  // Conditionally add the Contact Email column
  const columns = defaultColumns(
    isMobile,
    sweepstake,
    setSelectedSwitchId
  ).filter(
    (col) =>
      col.headerName !== 'Preferred Email' ||
      sweepstake.corporateAccountId !== null ||
      sweepstake.organizationId !== null
  )

  if (les.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        bgcolor={colorPrimitives.lightGray}
        padding={{ xs: 7 }}
        marginTop={{ xs: 2 }}
      >
        {/* <img src={notFoundLocation} alt="No data found icon" /> */}
        <img src={NotFoundLocation} />
        <Typography
          variant="label-form"
          marginTop={{ xs: 3 }}
          color="rgba(41, 43, 38, 0.60)"
        >
          No Participating Locations
        </Typography>
      </Box>
    )
  }

  return (
    <Box paddingTop={3}>
      <CorporateAccountsTreeDataGrid
        columns={columns}
        corporateAccounts={corporateAccounts}
        organizations={organizations}
        licensedEstablishments={les}
        hideFooter={les.length <= 20}
        initialState={{
          pagination: { paginationModel: { pageSize: 50 } },
        }}
        hideFooterSelectedRowCount
        pageSizeOptions={[50, 75, 100]}
        sx={{ maxWidth: '82.5vw' }}
        getRowClassName={() => '.white-background'}
        initialExpanded
        recalculateRows
      />
    </Box>
  )
}
